import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import {
  GAME_CATEGORIES,
  gameCategoryLabel,
  type CatalogueChangeReason,
  type CatalogueWriteResult,
  type GameCategory,
  type GamePictureTicket,
} from '@dayon/contracts';
import { api, ApiRequestError, isErrorCode } from '../../lib/api.ts';
import { ReasonFields } from './ReasonFields.tsx';
import { GamePictureSection } from './GamePictureSection.tsx';
import { hourlyImportName, providerOwnsTitle } from './provider-catalogue.ts';
import {
  catalogueRefusalTitle,
  DEFAULT_EDIT_REASON,
  noteIsValid,
  percentToBasisPoints,
} from './catalogue-write.ts';
import {
  combinedWrite,
  fieldsAnswerLost,
  notePlaceholder,
  partialSaveMessage,
  pictureAnswerLost,
  savedSentence,
  saveCount,
  storagePutRefusal,
  type StagedPicture,
} from './game-picture.ts';

/** The title being edited, as the drawer already holds it. */
export interface PendingGameConfig {
  id: string;
  code: string;
  name: string;
  category: string;
  subCategory: string | null;
  declaredRtpBp: string | null;
  provider: string;
  driver: string;
  /** The picture players see: an upload, else the provider's, else null and the tile draws initials. */
  iconUrl: string | null;
  /** The provider's own picture, kept aside while an upload shows. */
  providerIconUrl: string | null;
  /** Null: never touched by an operator. PROVIDER: an operator went back. UPLOADED: an upload shows. */
  iconSource: 'PROVIDER' | 'UPLOADED' | null;
  iconUpdatedAt: string | null;
  /** False when this installation has no picture store: Upload is off, going back still works. */
  uploadsReady: boolean;
}

/**
 * THE PICTURE'S OWN WRITE, the first half of Save.
 *
 * Going back is one request. An upload is three: a link, the bytes straight
 * from this browser to storage with EXACTLY the headers the link was signed
 * with (one dropped header voids the signature), then the confirm.
 *
 * THREE WAYS IT CAN FAIL, AND THEY MEAN DIFFERENT THINGS. A failed request for
 * the link is THROWN, as every `api` failure is: it writes nothing, so the
 * dialog's usual refusal is true whatever the cause. Storage's answer is
 * RETURNED as the popup to show, because it is not the platform's and carries
 * none of its codes — a PUT that threw is no answer at all, and 412 is a link
 * that was already used. A failure of the WRITE itself (the confirm, or going
 * back) is RETURNED too, so Save can tell a refusal from a lost answer: the
 * write may have committed before its answer was lost.
 */
type PictureOutcome =
  | { ok: true; result: CatalogueWriteResult }
  | { ok: false; popup: { title: string; message: string } }
  | { ok: false; writeFailed: unknown };

async function pictureWrite(call: () => Promise<CatalogueWriteResult>): Promise<PictureOutcome> {
  try {
    return { ok: true, result: await call() };
  } catch (error: unknown) {
    return { ok: false, writeFailed: error };
  }
}

async function writePicture(
  gameId: string,
  staged: StagedPicture,
  reason: { reasonCode: CatalogueChangeReason; note: string },
): Promise<PictureOutcome> {
  if (staged.kind === 'REVERT') {
    return pictureWrite(() => api.post<CatalogueWriteResult>(`/api/games/${gameId}/icon/revert`, reason));
  }
  const ticket = await api.post<GamePictureTicket>(`/api/games/${gameId}/icon/upload-ticket`, {
    contentType: staged.contentType,
    sizeBytes: staged.file.size,
  });
  let status: number | null = null;
  try {
    status = (await fetch(ticket.url, { method: 'PUT', headers: ticket.headers, body: staged.file })).status;
  } catch {
    /* No answer at all: status stays null, which storagePutRefusal reads as a lost upload. */
  }
  const lost = storagePutRefusal(status);
  if (lost) return { ok: false, popup: lost };
  return pictureWrite(() =>
    api.post<CatalogueWriteResult>(`/api/games/${gameId}/icon`, { key: ticket.key, ...reason }),
  );
}

/** The code an api failure carries; undefined for anything else. */
const codeOf = (error: unknown): string | undefined =>
  error instanceof ApiRequestError ? error.code : undefined;

/**
 * The dialog's usual refusal: the server's own sentence, under a heading picked
 * from what this session knows.
 */
function refusalPopup(
  error: unknown,
  subject: string,
  session: { allowed: boolean; mfa: boolean },
): { title: string; message: string; code?: string } {
  const raw = codeOf(error);
  return {
    title: catalogueRefusalTitle(isErrorCode(raw) ? raw : undefined, subject, session),
    message: error instanceof ApiRequestError ? error.message : 'Nothing was written. Try again shortly.',
    ...(raw ? { code: raw } : {}),
  };
}

/**
 * EDIT CONFIGURATION, for a title — `PATCH /api/games/:id/config`.
 *
 * ONLY WHAT MOVED IS SENT, and the form works that out by comparing each box
 * against what it was opened with. An untouched box is ABSENT from the request,
 * which is different from being sent as null: the route reads absent as "leave
 * this alone" and null as "clear this", and a form that could not express the
 * difference would wipe a declared RTP every time somebody edited only a name.
 *
 * TWO FIELDS ARE REFUSED ON SA- AND JILI-DRIVEN TITLES, and the reason is on
 * screen rather than waiting in a 400. `import-sa-hosts.ts` runs hourly and
 * upserts name and table type, and `import-jili-games.ts` runs hourly and
 * upserts name and table type — so an edit to either would be silently reverted
 * within the hour, leaving no trace of what undid it. Declared RTP and the platform
 * category are ours; the importer never writes them. SA's import also keeps SA's
 * picture in `provider_icon_url`, and replaces the one players see only while no
 * upload is showing — so the picture, unlike those two fields, IS editable here.
 *
 * THE PICTURE IS STAGED IN THIS DIALOG AND WRITTEN BY ITS ONE SAVE, under the
 * one reason it already asks for: two reason boxes in one dialog would be two
 * answers to one question. `GamePictureSection` only stages; no request leaves
 * this dialog before Save. Save writes the picture FIRST — for an upload, a
 * link from `POST /api/games/:id/icon/upload-ticket`, the browser's own PUT
 * straight to storage with exactly the headers the link was signed with (they
 * include If-None-Match: *, so a link only ever creates), then
 * `POST /api/games/:id/icon`; for going back, `POST /api/games/:id/icon/revert`
 * — and only then the PATCH for any fields that moved. A picture refused sends
 * nothing else. Fields refused after a saved picture get their own popup that
 * says the picture WAS saved, because pretending the whole Save failed would
 * send an operator to upload the same picture twice.
 *
 * THE CODE IS NOT HERE. It is the key the provider's catalogue is matched on:
 * changing it does not rename a title, it orphans the row from the provider's
 * own identifier and the next import creates a duplicate beside it.
 */
export function GameConfigDialog({
  pending,
  holdsKey,
  mfa,
  onClose,
  onChanged,
  onMaybeChanged,
}: {
  pending: PendingGameConfig | null;
  holdsKey: boolean;
  mfa: boolean;
  onClose: () => void;
  onChanged: (result: CatalogueWriteResult) => void;
  /**
   * A write got no answer (the picture's own, or a Save of fields alone), so it
   * may or may not have landed. There is no result to report; the screen closes
   * the dialog and re-reads, so the Configuration log the popup points to shows
   * what really happened.
   */
  onMaybeChanged: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<GameCategory>('SLOTS');
  const [subCategory, setSubCategory] = useState('');
  const [rtpPercent, setRtpPercent] = useState('');
  const [reasonCode, setReasonCode] = useState<CatalogueChangeReason>(DEFAULT_EDIT_REASON);
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /* A picture chosen, or a return to the provider's, waiting for Save. */
  const [staged, setStaged] = useState<StagedPicture | null>(null);

  /* Opened with the CURRENT values, so the diff below is against what is really
     stored rather than against an empty form. Basis points are shown as the
     percentage they were typed as. The staged picture is dropped on CLOSING as
     well as on opening: a picture chosen for one title must never be waiting in
     the dialog for the next one. */
  useEffect(() => {
    setStaged(null);
    if (!pending) return;
    setName(pending.name);
    setCategory(pending.category as GameCategory);
    setSubCategory(pending.subCategory ?? '');
    setRtpPercent(pending.declaredRtpBp === null ? '' : String(Number(pending.declaredRtpBp) / 100));
    setReasonCode(DEFAULT_EDIT_REASON);
    setNote('');
    setNoteTouched(false);
  }, [pending]);

  const providerOwned = providerOwnsTitle(pending?.driver);
  const rtp = percentToBasisPoints(rtpPercent);

  /*
   * THE DIFF, computed here and nowhere else.
   *
   * Each entry is either absent (unchanged) or a value the route will apply. An
   * empty box on a nullable field is `null` — "clear this" — and NOT absent,
   * because clearing a declared RTP is a real request an operator makes when a
   * certificate is withdrawn.
   */
  const patch = useMemo<Record<string, unknown>>(() => {
    const next: Record<string, unknown> = {};
    if (!pending) return next;
    if (!providerOwned && name.trim() !== pending.name) next['name'] = name.trim();
    if (category !== pending.category) next['category'] = category;
    if (!providerOwned) {
      const sub = subCategory.trim() === '' ? null : subCategory.trim().toLowerCase();
      if (sub !== pending.subCategory) next['subCategory'] = sub;
    }
    /* Parsed INSIDE the memo, from the raw string, so the dependency is the
       string an operator typed rather than a fresh result object every render. */
    const typed = percentToBasisPoints(rtpPercent);
    if (typed.kind === 'absent' && pending.declaredRtpBp !== null) next['declaredRtpBp'] = null;
    if (typed.kind === 'value' && String(typed.bp) !== pending.declaredRtpBp)
      next['declaredRtpBp'] = typed.bp;
    return next;
    /* MEMOISED, and not for speed: `submit` closes over this object, so a fresh
       one on every render rebuilds `submit` on every keystroke — which is what
       the exhaustive-deps rule caught. */
  }, [pending, providerOwned, name, category, subCategory, rtpPercent]);
  const movedCount = Object.keys(patch).length;
  /* The fields that moved, and one more for a staged picture: one Save, one count. */
  const count = saveCount(movedCount, staged);
  const ready =
    pending !== null &&
    count > 0 &&
    noteIsValid(note) &&
    rtp.kind !== 'invalid' &&
    (providerOwned || name.trim().length >= 2);

  const submit = useCallback(async () => {
    if (!pending || !ready) return;
    setSubmitting(true);
    const reason = { reasonCode, note: note.trim() };
    const results: CatalogueWriteResult[] = [];
    try {
      /* (1) THE PICTURE, FIRST. A refusal here — the server's, or storage's —
         stops the Save: nothing else is sent. */
      if (staged !== null) {
        const picture = await writePicture(pending.id, staged, reason);
        if (!picture.ok) {
          if ('popup' in picture) {
            showError(picture.popup);
            return;
          }
          const raw = codeOf(picture.writeFailed);
          const lost = pictureAnswerLost(raw);
          if (lost) {
            /* The picture may have changed: say so, and have the screen re-read
               so the Configuration log the sentence points to is current. */
            showError({ ...lost, ...(raw ? { code: raw } : {}) });
            onMaybeChanged();
            return;
          }
          showError(refusalPopup(picture.writeFailed, pending.name, { allowed: holdsKey, mfa }));
          return;
        }
        results.push(picture.result);
      }
      /* (2) THEN THE FIELDS, only when one moved. */
      if (movedCount > 0) {
        results.push(
          await api.patch<CatalogueWriteResult>(`/api/games/${pending.id}/config`, { ...patch, ...reason }),
        );
      }
      const [first, ...rest] = results;
      if (!first) return;
      toast(savedSentence([first, ...rest]));
      onChanged(combinedWrite([first, ...rest]));
      onClose();
    } catch (error: unknown) {
      const raw = codeOf(error);
      const [picture] = results;
      if (picture) {
        /* The picture is saved and stays saved; the fields were refused, or their
           answer was lost and they may have saved too. The screen hears about
           the picture so the table, the drawer and the log are re-read. */
        showError({
          ...partialSaveMessage({
            code: raw,
            message: error instanceof Error ? error.message : 'Try again shortly.',
          }),
          ...(raw ? { code: raw } : {}),
        });
        onChanged(picture);
        return;
      }
      /* With nothing staged, the failure was the fields' own write, and a lost
         answer means it may have landed: say so, and have the screen re-read the
         log the sentence points to. (With a picture staged and no result yet, the
         failure was the request for an upload link, which writes nothing.) */
      const lost = staged === null ? fieldsAnswerLost(raw) : null;
      if (lost) {
        showError({ ...lost, ...(raw ? { code: raw } : {}) });
        onMaybeChanged();
        return;
      }
      /* Nothing was written: the usual refusal. */
      showError(refusalPopup(error, pending.name, { allowed: holdsKey, mfa }));
    } finally {
      setSubmitting(false);
    }
  }, [
    pending,
    ready,
    staged,
    movedCount,
    patch,
    reasonCode,
    note,
    holdsKey,
    mfa,
    onChanged,
    onMaybeChanged,
    onClose,
    showError,
    toast,
  ]);

  return (
    <Dialog
      open={pending !== null}
      onClose={onClose}
      title={pending ? `Edit ${pending.name}` : ''}
      dismissible={!submitting}
      actions={
        <>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={submitting}
            disabled={!ready || submitting}
            onClick={() => void submit()}
          >
            {count === 0 ? 'Nothing to save' : `Save ${String(count)} change${count === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      {pending && (
        <div className="stack">
          {/* WHY THE BUTTON IS OFF, as visible text. "Nothing to save" names the
              state; this says what to do about it. */}
          {count === 0 && (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                Nothing has been changed yet. An edit that changes nothing is not recorded — the
                platform refuses it rather than writing a log row nobody made.
              </div>
            </div>
          )}

          {providerOwned && (
            <div className="notice">
              <Icon name="lock-simple" />
              <div>
                <strong>{pending.provider} supplies this title&rsquo;s name and table type.</strong>{' '}
                The {hourlyImportName(pending.driver)} overwrites both, so an edit here would be reverted within the
                hour with no trace of what undid it. Both boxes are therefore read-only. The
                platform category and the declared RTP are ours — the import never writes either —
                and they can be changed.
              </div>
            </div>
          )}

          <Field
            label="Display name"
            hint={
              providerOwned
                ? `Supplied by ${pending.provider} and overwritten hourly. Read-only here.`
                : 'What a player reads on the tile.'
            }
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={name}
                maxLength={160}
                readOnly={providerOwned}
                disabled={providerOwned}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>

          <Field label="Category" hint="The platform's own shelf, which the player portal filters on. Ours, not the provider's.">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={category}
                onChange={(e) => setCategory(e.target.value as GameCategory)}
              >
                {GAME_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {gameCategoryLabel(c)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Table type"
            hint={
              providerOwned
                ? `Supplied by ${pending.provider} and overwritten hourly. Read-only here.`
                : 'Optional. Empty clears it. Groups the tiles within a provider on the player’s shelf.'
            }
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={subCategory}
                maxLength={32}
                readOnly={providerOwned}
                disabled={providerOwned}
                onChange={(e) => setSubCategory(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Declared RTP (%)"
            error={rtp.kind === 'invalid' ? rtp.why : undefined}
            hint="The figure on the certificate. Empty clears it, which is not the same as 0% — the RTP deviation report measures against this."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="number"
                step="0.01"
                min="0"
                max="100"
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={rtpPercent}
                onChange={(e) => setRtpPercent(e.target.value)}
                placeholder="96.5"
              />
            )}
          </Field>

          <GamePictureSection game={pending} staged={staged} onStage={setStaged} disabled={submitting} />

          <ReasonFields
            reasonCode={reasonCode}
            onReasonCode={setReasonCode}
            note={note}
            onNote={setNote}
            noteTouched={noteTouched}
            onNoteBlur={() => setNoteTouched(true)}
            placeholder={notePlaceholder(movedCount, staged)}
          />
        </div>
      )}
    </Dialog>
  );
}
